// src/App.js
import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import styled, { ThemeProvider } from 'styled-components';
import { GlobalStyles, theme } from './styles/GlobalStyles';
import Navbar from './components/Navbar';
import Hero from './components/Hero';
import VoiceAgent from './components/VoiceAgent';
import Expertise from './components/Expertise';
import Products from './components/Products';
import Systems from './components/Systems';
import Work from './components/Work';
import About from './components/About';
import CallToAction from './components/CallToAction';
import Footer from './components/Footer';
import BookDownload from './components/BookDownload';
import CustomCursor from './components/ui/CustomCursor';

// Home page component with all sections
function HomePage() {
  return (
    <>
      <Hero />
      <VoiceAgent />
      <Work />
      <Products />
      <Expertise />
      <Systems />
      <About />
      <CallToAction />
    </>
  );
}

function App() {
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    // Simulate page loading
    setTimeout(() => {
      setIsLoaded(true);
    }, 1500);
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <GlobalStyles />
      <CustomCursor />
      <AnimatePresence>
        {!isLoaded ? (
          <Loader
            key="loader"
            initial={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          >
            <LoaderContent>
              <LoaderOrb
                animate={{ rotate: 360 }}
                transition={{ duration: 3, repeat: Infinity, ease: 'linear' }}
              />
              <YourLogo
                initial={{ opacity: 0, letterSpacing: '0.5em' }}
                animate={{ opacity: 1, letterSpacing: '0.15em' }}
                transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
              >
                ISABELLA
              </YourLogo>
              <LoaderText>Growth &amp; Marketing Leader · AI Product Builder</LoaderText>
              <LoaderTrack>
                <LoaderFill
                  initial={{ width: '0%' }}
                  animate={{ width: '100%' }}
                  transition={{ duration: 1.4, ease: 'easeInOut' }}
                />
              </LoaderTrack>
            </LoaderContent>
          </Loader>
        ) : (
          <MainContent
            key="main"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
          >
            <Router>
              <Navbar />
              <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/marketing-for-dummies" element={<BookDownload />} />
              </Routes>
              <Footer />
            </Router>
          </MainContent>
        )}
      </AnimatePresence>
    </ThemeProvider>
  );
}

// Styled components
const Loader = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
  background: ${({ theme }) => theme.colors.dark};
  z-index: 999;
`;

const LoaderContent = styled.div`
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const LoaderOrb = styled(motion.div)`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  margin-bottom: 1.75rem;
  background: conic-gradient(from 0deg, #6366f1, #8b5cf6, #ec4899, #06b6d4, #6366f1);
  box-shadow: 0 0 40px rgba(139, 92, 246, 0.5);
  mask: radial-gradient(farthest-side, transparent calc(100% - 7px), #000 calc(100% - 6px));
  -webkit-mask: radial-gradient(farthest-side, transparent calc(100% - 7px), #000 calc(100% - 6px));
`;

const YourLogo = styled(motion.h1)`
  background: linear-gradient(120deg, #6366f1, #8b5cf6, #ec4899);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  font-size: 2.75rem;
  font-weight: 800;
  margin-bottom: 0.75rem;
`;

const LoaderText = styled.p`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 0.9rem;
  margin-bottom: 1.5rem;
`;

const LoaderTrack = styled.div`
  width: 180px;
  height: 3px;
  border-radius: 3px;
  background: rgba(255, 255, 255, 0.08);
  overflow: hidden;
`;

const LoaderFill = styled(motion.div)`
  height: 100%;
  background: linear-gradient(90deg, #6366f1, #ec4899);
`;

const MainContent = styled(motion.div)`
  position: relative;
  overflow-x: hidden;
`;

export default App;